/**
 * File upload: validation, limits, and the upload itself.
 *
 * Moved from components/text-widget/utils/uploader.ts. Its twenty localised
 * error messages now resolve through the core's translator seam (./i18n)
 * rather than reaching into the widget's utils barrel; aliased to getLabel so
 * the call sites are untouched.
 */

import { FILE_UPLOAD_CONSTANTS } from './upload-constants';
import { getFileIcon as getFileIconFromUtils } from './file-icon';
import { label as getLabel } from '../host/i18n';

export interface UploadedFile {
  id: string;
  file: File;
  name: string;
  originalName: string;
  size: number;
  type: string;
  url: string;
  isImage: boolean;
  compressedFile?: File;
}

export interface FileUploadConfig {
  maxFiles?: number;
  maxFileSize?: number;
  allowedTypes?: string[];
}

const DEFAULT_CONFIG: FileUploadConfig = {
  maxFiles: FILE_UPLOAD_CONSTANTS.MAX_FILES,
  maxFileSize: FILE_UPLOAD_CONSTANTS.MAX_FILE_SIZE,
  allowedTypes: FILE_UPLOAD_CONSTANTS.SUPPORTED_TYPES,
};

export class FileUploadError extends Error {
  constructor(message: string, public code: string) {
    super(message);
    this.name = 'FileUploadError';
  }
}

export const generateFileId = (): string => {
  return Date.now().toString(36) + Math.random().toString(36).substring(2);
};

export const validateFilename = (filename: string): boolean => {
  const decodedFilename = decodeURIComponent(filename);

  if (!decodedFilename || decodedFilename.trim().length === 0) {
    throw new FileUploadError(getLabel('empty_filename', 'Filename cannot be empty.'), 'EMPTY_FILENAME');
  }

  if (decodedFilename.length > 255) {
    throw new FileUploadError(
      getLabel('filename_too_long', 'Filename is too long. Maximum 255 characters allowed.'),
      'FILENAME_TOO_LONG',
    );
  }

  if (decodedFilename.includes('\0') || /[\x00-\x1F\x7F]/.test(decodedFilename)) {
    throw new FileUploadError(
      getLabel('invalid_control_chars', 'Filename contains invalid control characters.'),
      'INVALID_CONTROL_CHARS',
    );
  }

  if (decodedFilename.includes('..')) {
    throw new FileUploadError(
      getLabel('directory_traversal', 'Filename cannot contain sequential periods.'),
      'DIRECTORY_TRAVERSAL',
    );
  }

  if (decodedFilename.includes('\\') || decodedFilename.includes('//')) {
    throw new FileUploadError(
      getLabel('path_traversal', 'Filename contains path traversal sequences.'),
      'PATH_TRAVERSAL',
    );
  }

  const lastDotIndex = decodedFilename.lastIndexOf('.');
  if (lastDotIndex > 0) {
    const nameWithoutExt = decodedFilename.substring(0, lastDotIndex);

    const commonExtensions = [
      'txt',
      'doc',
      'docx',
      'pdf',
      'jpg',
      'jpeg',
      'png',
      'gif',
      'zip',
      'tar',
      'gz',
      'bz2',
      'rar',
      '7z',
      'exe',
      'msi',
      'dmg',
      'deb',
      'rpm',
    ];

    const nameLower = nameWithoutExt.toLowerCase();
    const hasFakeExtension = commonExtensions.some(ext => nameLower.endsWith('.' + ext));

    if (hasFakeExtension) {
      throw new FileUploadError(
        getLabel('multiple_extensions', 'Filename contains multiple extensions which is not allowed.'),
        'MULTIPLE_EXTENSIONS',
      );
    }
  }

  const reservedNames = [
    'CON',
    'PRN',
    'AUX',
    'NUL',
    'COM1',
    'COM2',
    'COM3',
    'COM4',
    'COM5',
    'COM6',
    'COM7',
    'COM8',
    'COM9',
    'LPT1',
    'LPT2',
    'LPT3',
    'LPT4',
    'LPT5',
    'LPT6',
    'LPT7',
    'LPT8',
    'LPT9',
  ];
  const nameWithoutExt =
    lastDotIndex > 0 ? decodedFilename.substring(0, lastDotIndex).toUpperCase() : decodedFilename.toUpperCase();
  if (reservedNames.includes(nameWithoutExt)) {
    throw new FileUploadError(getLabel('reserved_name', 'Filename uses a reserved system name.'), 'RESERVED_NAME');
  }

  const safeFilenameRegex = /^[a-zA-Z0-9\s.\-()_\[\]#,]+$/;

  if (!safeFilenameRegex.test(decodedFilename)) {
    throw new FileUploadError(
      getLabel('invalid_filename', 'Please use a valid filename without special characters.'),
      'INVALID_FILENAME',
    );
  }

  if (decodedFilename.startsWith('.')) {
    throw new FileUploadError(
      getLabel('invalid_filename_start', 'Filename cannot start with a period, hyphen, or space.'),
      'INVALID_FILENAME_START',
    );
  }

  if (decodedFilename.startsWith('-') || decodedFilename.startsWith(' ')) {
    throw new FileUploadError(
      getLabel('invalid_filename_start', 'Filename cannot start with a period, hyphen, or space.'),
      'INVALID_FILENAME_START',
    );
  }

  if (decodedFilename.endsWith(' ')) {
    throw new FileUploadError(
      getLabel('invalid_filename_end', 'Filename cannot end with a space.'),
      'INVALID_FILENAME_END',
    );
  }

  if (/\s{3,}/.test(decodedFilename)) {
    throw new FileUploadError(
      getLabel('excessive_spaces', 'Filename cannot contain more than 2 consecutive spaces.'),
      'EXCESSIVE_SPACES',
    );
  }

  if (lastDotIndex <= 0 || lastDotIndex === decodedFilename.length - 1) {
    throw new FileUploadError(
      getLabel('invalid_filename_structure', 'Filename must have a valid name and extension (e.g., document.pdf).'),
      'INVALID_FILENAME_STRUCTURE',
    );
  }

  const namePart = decodedFilename.substring(0, lastDotIndex);
  const extensionPart = decodedFilename.substring(lastDotIndex + 1);

  if (namePart.length === 0 || extensionPart.length === 0) {
    throw new FileUploadError(
      getLabel('invalid_filename_structure', 'Filename must have a valid name and extension (e.g., document.pdf).'),
      'INVALID_FILENAME_STRUCTURE',
    );
  }

  return true;
};

export const validateFile = (file: File, config: FileUploadConfig = DEFAULT_CONFIG): boolean => {
  validateFilename(file.name);

  if (config.maxFileSize && file.size > config.maxFileSize) {
    throw new FileUploadError(
      getLabel('file_too_large', 'File size exceeds the maximum limit of 25MB.'),
      'FILE_TOO_LARGE',
    );
  }

  if (config.allowedTypes && config.allowedTypes.length > 0) {
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    const mimeType = file.type.toLowerCase();

    const isAllowed = config.allowedTypes.some(type => {
      if (type.startsWith('.')) {
        return fileExtension === type.toLowerCase();
      }
      if (type.includes('*')) {
        const baseType = type.split('/')[0];
        return mimeType.startsWith(baseType + '/');
      }
      return mimeType === type;
    });

    if (!isAllowed) {
      throw new FileUploadError(
        getLabel('unsupported_file_type', 'File type not supported. Please use images or PDFs only.'),
        'UNSUPPORTED_FILE_TYPE',
      );
    }
  }

  return true;
};

export const validateFiles = (files: FileList | File[], config: FileUploadConfig = DEFAULT_CONFIG): void => {
  for (const file of files) {
    validateFile(file, config);
  }
};

export const checkFileDuplicate = (
  file: File,
  existingFiles: Array<{ name: string; size: number; type: string }>,
): boolean => {
  return existingFiles.some(
    existingFile =>
      existingFile.name === file.name && existingFile.size === file.size && existingFile.type === file.type,
  );
};

export const checkFileDuplicateEnhanced = (
  file: File,
  existingFiles: Array<{ name: string; originalName?: string; size: number; type: string; lastModified?: number }>,
): boolean => {
  const exactMatch = existingFiles.some(existingFile => {
    const existingFileName = existingFile.originalName || existingFile.name;
    return existingFileName === file.name && existingFile.size === file.size && existingFile.type === file.type;
  });

  if (exactMatch) return true;

  const nameTypeMatch = existingFiles.some(existingFile => {
    const existingFileName = existingFile.originalName || existingFile.name;
    return existingFileName === file.name && existingFile.type === file.type;
  });

  if (nameTypeMatch) return true;

  return false;
};

export const processFiles = async (
  files: FileList | File[],
  config: FileUploadConfig = DEFAULT_CONFIG,
): Promise<UploadedFile[]> => {
  const fileArray = Array.from(files);
  const processedFiles: UploadedFile[] = [];

  for (const file of fileArray) {
    try {
      validateFile(file, config);

      let processedFile = file;

      const uploadedFile: UploadedFile = {
        id: generateFileId(),
        file: processedFile,
        name: processedFile.name,
        originalName: processedFile.name,
        size: processedFile.size,
        type: processedFile.type,
        url: URL.createObjectURL(processedFile),
        isImage: processedFile.type.startsWith('image/'),
        compressedFile: processedFile !== file ? processedFile : undefined,
      };

      processedFiles.push(uploadedFile);
    } catch (error) {}
  }

  return processedFiles;
};

export const getFileIcon = (file: UploadedFile): string => {
  if (file.isImage || file.type.startsWith('image/')) {
    return '🖼️';
  }
  return getFileIconFromUtils(file.originalName);
};

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export const cleanupFileUrls = (files: UploadedFile[]): void => {
  files.forEach(file => {
    if (file.url && file.url.startsWith('blob:')) {
      URL.revokeObjectURL(file.url);
    }
  });
};

export const filesToFormData = (files: UploadedFile[]): FormData => {
  const formData = new FormData();

  files.forEach((file, index) => {
    formData.append(`file_${index}`, file.file);
    formData.append(`file_${index}_name`, file.name);
    formData.append(`file_${index}_type`, file.type);
    formData.append(`file_${index}_size`, file.size.toString());
  });

  return formData;
};

export const readFileAsDataUrl = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      resolve(reader.result as string);
    };

    reader.onerror = () => {
      reject(
        new FileUploadError(getLabel('file_read_failed', 'Failed to read files. Please try again.'), 'FILE_READ_ERROR'),
      );
    };

    reader.readAsDataURL(file);
  });
};

export interface FileValidationResult {
  file: File;
  meta: { id: string; name: string; contentType: string };
}

export interface FileValidationOptions {
  config?: FileUploadConfig;
  existingFiles?: Array<{ name: string; size: number; type: string }>;
  maxFiles?: number;
  generateId?: () => string;
}

export interface FileValidationError {
  file: File;
  error: FileUploadError;
}

export interface FileValidationResponse {
  validFiles: FileValidationResult[];
  invalidFiles: FileValidationError[];
  errors: string[];
}

export const validateAndPrepareFiles = (
  files: FileList | File[],
  options: FileValidationOptions = {},
): FileValidationResponse => {
  const { config = DEFAULT_CONFIG, existingFiles = [], generateId = generateFileId } = options;

  const validFiles: FileValidationResult[] = [];
  const invalidFiles: FileValidationError[] = [];
  const errors: string[] = [];

  for (let i = 0; i < files.length; i++) {
    const file = files[i];

    try {
      validateFile(file, config);

      if (checkFileDuplicateEnhanced(file, existingFiles)) {
        const duplicateMsg = getLabel('duplicate_file', 'File has already been attached.');
        invalidFiles.push({
          file,
          error: new FileUploadError(duplicateMsg, 'DUPLICATE_FILE'),
        });
        errors.push(`${file.name}: ${duplicateMsg}`);
        continue;
      }

      const duplicateInNewFiles = validFiles.some(
        newFile => newFile.meta.name === file.name && newFile.meta.contentType === file.type,
      );

      if (duplicateInNewFiles) {
        const duplicateMsg = getLabel('duplicate_file', 'File has already been attached.');
        invalidFiles.push({
          file,
          error: new FileUploadError(duplicateMsg, 'DUPLICATE_FILE'),
        });
        errors.push(`${file.name}: ${duplicateMsg}`);
        continue;
      }

      const tempId = generateId();
      validFiles.push({
        file,
        meta: { id: tempId, name: file.name, contentType: file.type },
      });
    } catch (err) {
      const error = err instanceof FileUploadError ? err : new FileUploadError(String(err), 'VALIDATION_ERROR');
      invalidFiles.push({ file, error });
      errors.push(`${file.name}: ${error.message}`);
    }
  }

  return {
    validFiles,
    invalidFiles,
    errors,
  };
};
